/**
 * Returns whether the MIME type appears in the list of accepted types. Allows
 * wildcard types in the format "image/*".
 */
export function isValidMimeType(actual: string, accept: string[]): boolean {
    if (accept.length === 0) {
        return true;
    }

    return accept.some(mimeType => {
        if (mimeType.endsWith('/*')) {
            const [type] = mimeType.split('/');

            return actual.startsWith(`${type}/`);
        }

        return actual === mimeType;
    });
}

/**
 * Returns whether an image upload is accepted.
 */
export function acceptsImage(mimeTypes: string[]): boolean {
    return isValidMimeType('image/*', mimeTypes);
}

/**
 * Converts a size in bytes to a human-readable string.
 */
export function fileSize(bytes: number): string {
    const mb = (bytes / (1024 ** 2)).toFixed(2)
        .replace(/0+$/, '')
        .replace(/\.$/, '');

    return `${mb} MB`;
}

/**
 * Captures a still image from the video element.
 */
export function captureImage(
    video: HTMLVideoElement,
    canvas: HTMLCanvasElement,
    type: string = 'image/jpeg',
    quality: number = 0.8
): Promise<Blob> {
    return new Promise((resolve, reject) => {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext('2d')!.drawImage(video, 0, 0);
        canvas.toBlob(blob => {
            if (blob === null) {
                reject();
            } else {
                resolve(blob);
            }
        }, type, quality);
    });
}

/**
 * Returns whether the browser supports media capture.
 */
export function supportsMediaCapture() {
    return 'mediaDevices' in navigator && 'getUserMedia' in navigator.mediaDevices;
}
